import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

const STAGES = [
  ["draft", "Draft"],
  ["pending_confirmation", "Waiting for the seller to confirm"],
  ["requested", "Collection requested"],
  ["assigned", "Agent assigned"],
  ["collected", "Collected and weighed"],
  ["awaiting_payment", "Awaiting payment"],
  ["paid", "Paid"],
];

function agentName(agent) {
  return (
    agent?.agentDetails?.agent_name ||
    [agent?.firstName, agent?.lastName].filter(Boolean).join(" ") ||
    agent?.username ||
    "Agent"
  );
}

function explainLoadError(error) {
  if (!error?.response) {
    return "Jhonks could not reach the server. Start Jhonks-server, then refresh this page.";
  }
  if (error.response.status === 404) {
    return "This server does not have Operations yet. The admin page must talk to the updated Jhonks server.";
  }
  if (error.response.status === 401 || error.response.status === 403) {
    return "Sign in with an admin account to open Operations.";
  }
  return error.response.data?.message || "Could not load operations settings.";
}

const OperationsAdmin = () => {
  const [tab, setTab] = useState("matching");
  const [settings, setSettings] = useState(null);
  const [agents, setAgents] = useState([]);
  const [fallbackIds, setFallbackIds] = useState([]);
  const [query, setQuery] = useState("");
  const [sessions, setSessions] = useState([]);
  const [whatsappStats, setWhatsappStats] = useState(null);
  const [threads, setThreads] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [article, setArticle] = useState({ title: "", body: "", href: "", active: true });
  const [reply, setReply] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    const response = await api.get("/admin/platform/settings");
    const next = response.data.data.settings;
    setSettings(next);
    setAgents(response.data.data.agents || []);
    setFallbackIds(next.fallbackAgents || []);
    setError("");
  };

  useEffect(() => {
    loadSettings().catch((loadError) => setError(explainLoadError(loadError)));
  }, []);

  useEffect(() => {
    if (tab === "whatsapp") {
      api
        .get("/admin/platform/whatsapp")
        .then((response) => {
          setSessions(response.data.data.sessions || []);
          setWhatsappStats(response.data.data.stats || null);
        })
        .catch(() => setError("Could not load WhatsApp orders."));
    }
    if (tab === "support") {
      api
        .get("/admin/platform/support")
        .then((response) => {
          const next = response.data.data.threads || [];
          setThreads(next);
          setSelectedThread((current) => next.find((thread) => thread._id === current?._id) || next[0] || null);
        })
        .catch(() => setError("Could not load support conversations."));
      api
        .get("/admin/platform/articles")
        .then((response) => setArticles(response.data.data.articles || []))
        .catch(() => {});
    }
  }, [tab]);

  const orderedFallbacks = useMemo(
    () => fallbackIds.map((id) => agents.find((agent) => agent._id === id)).filter(Boolean),
    [agents, fallbackIds]
  );

  const visibleAgents = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return agents;
    return agents.filter((agent) =>
      `${agentName(agent)} ${agent.state || ""} ${agent.agentDetails?.localGovernmentAddress || ""} ${agent.phoneNumber || ""}`
        .toLowerCase()
        .includes(needle)
    );
  }, [agents, query]);

  const toggleFallback = (id) => {
    setFallbackIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const moveFallback = (id, direction) => {
    setFallbackIds((current) => {
      const index = current.indexOf(id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const copy = [...current];
      const [item] = copy.splice(index, 1);
      copy.splice(nextIndex, 0, item);
      return copy;
    });
  };

  const persist = async (body, success) => {
    setSaving(true);
    setError("");
    try {
      await api.put("/admin/platform/settings", body);
      setMessage(success);
      await loadSettings();
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const saveMatching = async (event) => {
    event.preventDefault();
    if (!fallbackIds.length) {
      setError("Choose at least one fallback agent. Jhonks must always have someone to assign.");
      return;
    }
    await persist(
      { ...settings, fallbackAgents: fallbackIds },
      "Fallback agents saved. A sale with no nearby match will use this list."
    );
  };

  const saveWhatsApp = async (event) => {
    event.preventDefault();
    await persist(
      {
        whatsappEnabled: settings.whatsappEnabled,
        whatsappDisplayNumber: settings.whatsappDisplayNumber,
        whatsappPhoneNumberId: settings.whatsappPhoneNumberId,
        whatsappVerifyToken: settings.whatsappVerifyToken,
        whatsappAccessToken: settings.whatsappAccessToken,
      },
      "WhatsApp settings saved."
    );
  };

  const saveOrder = async (id, body) => {
    try {
      await api.patch(`/admin/platform/whatsapp/${id}`, body);
      const response = await api.get("/admin/platform/whatsapp");
      setSessions(response.data.data.sessions || []);
      setWhatsappStats(response.data.data.stats || null);
      setMessage("WhatsApp order updated.");
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Could not update this WhatsApp order.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-green-700 to-green-900 p-6 text-white shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-green-100">Jhonks operations</p>
        <h1 className="mt-2 text-3xl font-bold">Run matching, WhatsApp, and support</h1>
        <p className="mt-2 max-w-2xl text-sm text-green-50">
          Nearby agents are chosen first. When nobody is close, Jhonks assigns the best agent from the fallback list you set here, and it always assigns one of them.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {[
            ["matching", "Agent matching"],
            ["whatsapp", "WhatsApp"],
            ["support", "Support inbox"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setTab(id);
                setMessage("");
                setError("");
              }}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                tab === id ? "bg-white text-green-800" : "bg-white/15 text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {message && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{message}</div>
      )}

      {tab === "matching" && !settings && !error && (
        <div className="rounded-3xl bg-white p-8 text-sm text-gray-500 shadow-sm">Loading agents…</div>
      )}

      {tab === "matching" && settings && (
        <div className="space-y-4">
        <form onSubmit={saveMatching} className="grid gap-4 lg:grid-cols-[1.4fr_0.8fr]">
          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">All collection agents</h2>
                <p className="mt-1 text-sm text-gray-500">Tick the agents who can take a sale when there is no nearby match.</p>
              </div>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search name, state, or area"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm sm:w-64"
              />
            </div>
            <div className="mt-4 max-h-[32rem] space-y-2 overflow-y-auto pr-1">
              {visibleAgents.map((agent) => {
                const selected = fallbackIds.includes(agent._id);
                return (
                  <label
                    key={agent._id}
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 ${
                      selected ? "border-green-600 bg-green-50" : "border-gray-200 bg-white"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selected}
                      onChange={() => toggleFallback(agent._id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900">{agentName(agent)}</span>
                        {agent.agentDetails?.isVerified && (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                            Verified
                          </span>
                        )}
                      </span>
                      <span className="mt-1 block text-sm text-gray-500">
                        {[agent.state, agent.agentDetails?.localGovernmentAddress, agent.phoneNumber]
                          .filter(Boolean)
                          .join(" · ") || "Location not set"}
                      </span>
                    </span>
                  </label>
                );
              })}
              {!agents.length && (
                <p className="rounded-2xl bg-gray-50 px-4 py-6 text-sm text-gray-500">
                  There are no agent accounts yet. Approve an agent, then come back and add them to this list.
                </p>
              )}
            </div>
          </section>

          <div className="space-y-4">
            <section className="rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900">Fallback order</h2>
              <p className="mt-1 text-sm text-gray-500">
                If several fallback agents fit, Jhonks picks the closest. If none fit the seller’s area, it assigns number 1.
              </p>
              <ol className="mt-4 space-y-2">
                {orderedFallbacks.map((agent, index) => (
                  <li key={agent._id} className="flex items-center gap-2 rounded-2xl bg-gray-50 px-3 py-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-700 text-sm font-bold text-white">
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-gray-900">{agentName(agent)}</span>
                      <span className="block truncate text-xs text-gray-500">{agent.state || "State not set"}</span>
                    </span>
                    <button type="button" className="text-xs font-semibold text-gray-600" onClick={() => moveFallback(agent._id, -1)}>
                      Up
                    </button>
                    <button type="button" className="text-xs font-semibold text-gray-600" onClick={() => moveFallback(agent._id, 1)}>
                      Down
                    </button>
                  </li>
                ))}
              </ol>
              {!orderedFallbacks.length && (
                <p className="mt-4 rounded-2xl border border-dashed border-amber-300 bg-amber-50 px-3 py-4 text-sm text-amber-900">
                  No fallback agents yet. Tick at least one agent on the left. A sale with no nearby match cannot proceed until this list has someone on it.
                </p>
              )}
            </section>

            <section className="rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900">Gemini</h2>
              <p className="mt-1 text-sm text-gray-500">
                {settings.geminiConfigured
                  ? `A key is saved (${settings.geminiApiKeyMasked}). Gemini ranks close agents. Leave this blank to keep it.`
                  : "No key yet. Matching still works from location, then this fallback list."}
              </p>
              <input
                className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                placeholder="Paste a new Gemini API key"
                onChange={(event) => setSettings({ ...settings, geminiApiKey: event.target.value })}
              />
              <button
                className="mt-4 w-full rounded-xl bg-green-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save matching settings"}
              </button>
            </section>
          </div>
        </form>
        <form
          className="rounded-3xl bg-white p-6 shadow-sm"
          onSubmit={async (event) => {
            event.preventDefault();
            await persist(
              {
                otpHourlyLimit: Number(settings.otpHourlyLimit),
                otpResendCooldownSeconds: Number(settings.otpResendCooldownSeconds),
              },
              "Verification code limits saved."
            );
          }}
        >
          <h2 className="text-lg font-bold text-gray-900">Verification codes</h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Each phone number can request a limited number of codes per hour, and must wait before asking again. This keeps an inactive number from spending the WhatsApp and text-message balance. The usual setting is 5 codes an hour, with 60 seconds between requests.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-gray-700">
              Codes per hour
              <input
                type="number"
                min="1"
                max="15"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"
                value={settings.otpHourlyLimit ?? 5}
                onChange={(event) => setSettings({ ...settings, otpHourlyLimit: event.target.value })}
              />
            </label>
            <label className="text-sm text-gray-700">
              Seconds between requests
              <input
                type="number"
                min="30"
                max="300"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"
                value={settings.otpResendCooldownSeconds ?? 60}
                onChange={(event) => setSettings({ ...settings, otpResendCooldownSeconds: event.target.value })}
              />
            </label>
          </div>
          <button
            className="mt-4 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            type="submit"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save code limits"}
          </button>
        </form>
        </div>
      )}

      {tab === "whatsapp" && settings && (
        <div className="space-y-4">
          <form onSubmit={saveWhatsApp} className="rounded-3xl bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900">WhatsApp Business</h2>
            <p className="mt-1 text-sm text-gray-500">
              Webhook: https://backend.jhonks.com/api/whatsapp/webhook. A seller who messages this number gets a Jhonks account on that phone.
            </p>
            <label className="mt-4 flex items-center gap-2 text-sm font-medium text-gray-800">
              <input
                type="checkbox"
                checked={Boolean(settings.whatsappEnabled)}
                onChange={(event) => setSettings({ ...settings, whatsappEnabled: event.target.checked })}
              />
              Show Continue on WhatsApp and accept messages
            </label>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input className="rounded-xl border px-3 py-2 text-sm" placeholder="Display number, digits only" value={settings.whatsappDisplayNumber || ""} onChange={(event) => setSettings({ ...settings, whatsappDisplayNumber: event.target.value })} />
              <input className="rounded-xl border px-3 py-2 text-sm" placeholder="Phone number ID" value={settings.whatsappPhoneNumberId || ""} onChange={(event) => setSettings({ ...settings, whatsappPhoneNumberId: event.target.value })} />
              <input className="rounded-xl border px-3 py-2 text-sm" placeholder="Verify token" value={settings.whatsappVerifyToken || ""} onChange={(event) => setSettings({ ...settings, whatsappVerifyToken: event.target.value })} />
              <input className="rounded-xl border px-3 py-2 text-sm" placeholder={settings.whatsappAccessTokenMasked ? `Token ${settings.whatsappAccessTokenMasked}` : "Cloud API token"} onChange={(event) => setSettings({ ...settings, whatsappAccessToken: event.target.value })} />
            </div>
            <button className="mt-4 rounded-xl bg-green-700 px-4 py-2 text-sm font-semibold text-white" type="submit">
              Save WhatsApp
            </button>
          </form>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ["Connected on WhatsApp", whatsappStats?.connected ?? 0, "Phone numbers that have chatted with Jhonks"],
              ["WhatsApp accounts", whatsappStats?.accountsCreatedOnWhatsapp ?? 0, "Accounts first created from a WhatsApp chat"],
              ["Open orders", whatsappStats?.openOrders ?? 0, "Chats waiting on an agent, a sale, or payment"],
              ["Paid on WhatsApp", whatsappStats?.paid ?? 0, "Chats whose sale is already paid"],
            ].map(([label, value, detail]) => (
              <div key={label} className="rounded-3xl bg-white p-5 shadow-sm">
                <p className="text-sm font-medium text-gray-500">{label}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
                <p className="mt-2 text-xs text-gray-500">{detail}</p>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            {sessions.map((session) => (
              <OrderCard key={session._id} session={session} onSave={saveOrder} />
            ))}
            {!sessions.length && (
              <p className="rounded-3xl bg-white px-6 py-8 text-sm text-gray-500 shadow-sm">No WhatsApp orders yet.</p>
            )}
          </div>
        </div>
      )}

      {tab === "support" && (
        <div className="grid gap-4 xl:grid-cols-[280px_1fr_320px]">
          <section className="rounded-3xl bg-white p-4 shadow-sm">
            <h2 className="px-2 text-sm font-bold uppercase tracking-wide text-gray-500">Conversations</h2>
            <div className="mt-3 max-h-[36rem] space-y-2 overflow-y-auto">
              {threads.map((thread) => (
                <button
                  key={thread._id}
                  type="button"
                  onClick={() => {
                    setSelectedThread(thread);
                    setReply("");
                  }}
                  className={`w-full rounded-2xl px-3 py-3 text-left ${
                    selectedThread?._id === thread._id ? "bg-green-700 text-white" : "bg-gray-50 text-gray-900"
                  }`}
                >
                  <span className="block text-sm font-semibold">
                    {thread.user?.firstName || "User"} {thread.user?.lastName || ""}
                  </span>
                  <span className={`mt-1 block text-xs ${selectedThread?._id === thread._id ? "text-green-100" : "text-gray-500"}`}>
                    {thread.mode === "human" ? "Waiting for a person" : "Assistant"} · {thread.status}
                  </span>
                </button>
              ))}
              {!threads.length && <p className="px-2 text-sm text-gray-500">No support conversations yet.</p>}
            </div>
          </section>

          <section className="flex min-h-[32rem] flex-col rounded-3xl bg-white p-5 shadow-sm">
            {selectedThread ? (
              <>
                <div className="border-b border-gray-100 pb-3">
                  <h2 className="text-lg font-bold text-gray-900">
                    {selectedThread.user?.firstName || "User"} {selectedThread.user?.lastName || ""}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {selectedThread.user?.phoneNumber || selectedThread.user?.email || "No contact on file"}
                  </p>
                </div>
                <div className="mt-4 flex-1 space-y-3 overflow-y-auto">
                  {(selectedThread.messages || []).map((item, index) => (
                    <div
                      key={index}
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                        item.from === "user" ? "ml-auto bg-green-700 text-white" : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      <p className="mb-1 text-xs font-semibold opacity-70">
                        {item.from === "user" ? "Customer" : item.from === "human" ? "You" : "Assistant"}
                      </p>
                      {item.text}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <input
                    className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Reply as Jhonks support"
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                  />
                  <button
                    type="button"
                    className="rounded-xl bg-green-700 px-4 py-2 text-sm font-semibold text-white"
                    onClick={async () => {
                      if (!reply.trim()) return;
                      await api.post(`/admin/platform/support/${selectedThread._id}/reply`, { text: reply });
                      setReply("");
                      const response = await api.get("/admin/platform/support");
                      const next = response.data.data.threads || [];
                      setThreads(next);
                      setSelectedThread(next.find((thread) => thread._id === selectedThread._id) || null);
                    }}
                  >
                    Send
                  </button>
                </div>
              </>
            ) : (
              <p className="m-auto text-sm text-gray-500">Select a conversation.</p>
            )}
          </section>

          <section className="space-y-4">
            <form
              className="space-y-3 rounded-3xl bg-white p-5 shadow-sm"
              onSubmit={async (event) => {
                event.preventDefault();
                await persist(
                  { supportInstructions: settings?.supportInstructions || "" },
                  "Support guidance saved."
                );
              }}
            >
              <h2 className="text-lg font-bold text-gray-900">How the assistant should sound</h2>
              <textarea
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                rows={5}
                value={settings?.supportInstructions || ""}
                onChange={(event) => setSettings({ ...settings, supportInstructions: event.target.value })}
              />
              <button className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white" type="submit">
                Save guidance
              </button>
            </form>
            <form
              className="space-y-3 rounded-3xl bg-white p-5 shadow-sm"
              onSubmit={async (event) => {
                event.preventDefault();
                await api.post("/admin/platform/articles", article);
                setArticle({ title: "", body: "", href: "", active: true });
                const response = await api.get("/admin/platform/articles");
                setArticles(response.data.data.articles || []);
              }}
            >
              <h2 className="text-lg font-bold text-gray-900">Knowledge for the assistant</h2>
              <input className="w-full rounded-xl border px-3 py-2 text-sm" placeholder="Title" value={article.title} onChange={(event) => setArticle({ ...article, title: event.target.value })} />
              <textarea className="w-full rounded-xl border px-3 py-2 text-sm" rows={4} placeholder="What the assistant should know" value={article.body} onChange={(event) => setArticle({ ...article, body: event.target.value })} />
              <input className="w-full rounded-xl border px-3 py-2 text-sm" placeholder="Page link, for example /bin" value={article.href} onChange={(event) => setArticle({ ...article, href: event.target.value })} />
              <button className="rounded-xl bg-green-700 px-4 py-2 text-sm font-semibold text-white" type="submit">
                Add article
              </button>
              <ul className="space-y-2 text-sm">
                {articles.map((item) => (
                  <li key={item._id} className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2">
                    <span className="font-medium text-gray-800">{item.title}</span>
                    <button
                      type="button"
                      className="text-xs font-semibold text-red-600"
                      onClick={async () => {
                        await api.delete(`/admin/platform/articles/${item._id}`);
                        setArticles(articles.filter((row) => row._id !== item._id));
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

function OrderCard({ session, onSave }) {
  const [stage, setStage] = useState(session.stage);
  const [items, setItems] = useState(session.items || []);
  const [accountNumber, setAccountNumber] = useState(session.accountNumber || "");
  const [bankName, setBankName] = useState(session.bankName || "");
  const [accountName, setAccountName] = useState(session.accountName || "");
  const stageLabel = STAGES.find(([id]) => id === session.stage)?.[1] || session.stage;

  return (
    <article className="rounded-3xl bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{session.profileName || session.waId}</h3>
          <p className="text-sm text-gray-500">
            {session.waId} · {stageLabel}
            {session.agent ? ` · ${agentName(session.agent)}` : ""}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            The assigned agent confirms the kilograms in their agent account. The seller then taps Sell on WhatsApp. Mark the sale paid from Sales after you send the money.
          </p>
        </div>
        <select className="rounded-xl border px-3 py-2 text-sm" value={stage} onChange={(event) => setStage(event.target.value)}>
          {STAGES.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>
      <div className="mt-4 space-y-2">
        {items.map((item, index) => (
          <label key={`${item.name}-${index}`} className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-gray-800">{item.name || "Material"}</span>
            <span className="flex items-center gap-2 text-gray-500">
              <input
                type="number"
                min="0"
                step="0.1"
                className="w-24 rounded-lg border px-2 py-1 text-right"
                value={item.quantity ?? ""}
                onChange={(event) => {
                  const next = [...items];
                  next[index] = { ...item, quantity: Number(event.target.value) };
                  setItems(next);
                }}
              />
              kg
            </span>
          </label>
        ))}
        {!items.length && <p className="text-sm text-gray-500">No materials on this order yet.</p>}
      </div>
      <div className="mt-4 grid gap-2 md:grid-cols-3">
        <input className="rounded-xl border px-3 py-2 text-sm" placeholder="Account name" value={accountName} onChange={(event) => setAccountName(event.target.value)} />
        <input className="rounded-xl border px-3 py-2 text-sm" placeholder="Account number" value={accountNumber} onChange={(event) => setAccountNumber(event.target.value)} />
        <input className="rounded-xl border px-3 py-2 text-sm" placeholder="Bank" value={bankName} onChange={(event) => setBankName(event.target.value)} />
      </div>
      <button
        type="button"
        className="mt-4 rounded-xl bg-green-700 px-4 py-2 text-sm font-semibold text-white"
        onClick={() => onSave(session._id, { stage, items, accountNumber, bankName, accountName })}
      >
        Save order
      </button>
    </article>
  );
}

export default OperationsAdmin;
